import * as api from "@/api";
import Ssl from "@/classes/ssl";
import { redo, undo, wrap } from "@/code-editor";
import styles from "@/components/main-menu.module.css";
import {
	configExists,
	fileExists,
	getValidFilename,
	handleSaveClick,
	openFile,
	save,
} from "@/file";
import globals from "@/globals";
import { showSettings } from "@/settings";
import * as signals from "@/signals";
import { getDomain, saveHostSettings } from "@/site";
import { PresetSaveCallback } from "@/types";
import {
	getPremium,
	getSupportLink,
	handleKeyboardClick,
	isPremium,
	openPopup,
	showDialog,
	showPrompt,
	sleep,
} from "@/utils";
import { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import { faGithub } from "@fortawesome/free-brands-svg-icons";
import {
	faArrowRightArrowLeft,
	faBan,
	faCircleInfo,
	faCircleQuestion,
	faCircleUser,
	faGear,
	faHouseLaptop,
	faKey,
	faLock,
	faPowerOff,
	faRedo,
	faRightFromBracket,
	faSave,
	faShieldHalved,
	faSquareArrowUpRight,
	faUndo,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Dialog } from "@headlessui/react";
import clsx from "clsx";
import { t } from "i18next";
import md5 from "md5";
import { JSX, MouseEvent, useState } from "react";

interface Divider {
	divider: true;
	hideOnMobile?: boolean;
	when: boolean;
}

interface MainMenuItemInfo {
	hasPopup?: "dialog" | "menu";
	hideOnMobile?: boolean;
	icon: IconDefinition;
	link?: string;
	onClick?: (event?: React.MouseEvent) => void | Promise<void>;
	requireSiteOpened?: boolean;
	shortcut?: string;
	text: string;
	when: boolean;
}

function MainMenu(): JSX.Element {
	const [isClosing, setIsClosing] = useState(false);

	const closeMenu = async (): Promise<void> => {
		if (isClosing || !signals.isMainMenuShown.value) {
			return;
		}
		setIsClosing(true);
		await sleep(globals.ANIMATION_WAIT_TIME);
		signals.isMainMenuShown.value = false;
		setIsClosing(false);
	};

	const isDivider = (item: MainMenuItemInfo | Divider): item is Divider => {
		return (item as Divider).divider;
	};

	const menuItems: (MainMenuItemInfo | Divider)[] = [
		{
			hasPopup: "dialog",
			icon: faRightFromBracket,
			onClick: async (): Promise<void> => {
				await showDialog(t("confirmLogOut"), {
					showCancel: true,
				});
				signals.isLoadingShown.value = true;
				try {
					await api.logOut();
					localStorage.clear();
					location.reload();
				} catch (error) {
					void api.handleApiError(error);
				} finally {
					signals.isLoadingShown.value = false;
				}
			},
			text: t("logOut"),
			when: !!signals.login.value?.username,
		},
		{
			divider: true,
			when: !!signals.login.value?.username,
		},
		{
			hideOnMobile: true,
			icon: faSave,
			onClick: handleSaveClick,
			requireSiteOpened: true,
			shortcut: (globals.isMac ? "⌘" : "Ctrl+") + "S",
			text: t("save"),
			when:
				!signals.currentFile.value ||
				typeof signals.files.value[signals.currentFile.value] ===
					"string",
		},
		{
			icon: faSave,
			onClick: (): void => {
				if (isPremium()) {
					void save({
						callback: PresetSaveCallback.SHOW_DIALOG,
					});
				}
			},
			requireSiteOpened: true,
			text: t("saveAll"),
			when: !signals.currentFile.value?.endsWith(".md"),
		},
		{
			icon: faUndo,
			onClick: undo,
			shortcut: (globals.isMac ? "⌘" : "Ctrl+") + "Z",
			text: t("undo"),
			when: true,
		},
		{
			icon: faRedo,
			onClick: redo,
			shortcut: ((): string => {
				if (globals.isMac) {
					return "⇧⌘Z";
				}
				if (globals.isWindows) {
					return "Ctrl+Y";
				}
				return "Ctrl+Shift+Z";
			})(),
			text: t("redo"),
			when: true,
		},
		{
			hideOnMobile: true,
			icon: faArrowRightArrowLeft,
			onClick: wrap,
			shortcut: (globals.isMac ? "⌥" : "Alt+") + "Z",
			text: t("wordWrap"),
			when: true,
		},
		{
			divider: true,
			when: true,
		},
		{
			icon: faGithub,
			onClick: (): void => {
				window.open("https://docs.retiehe.com/host/auto-deploy.html");
			},
			text: t("gitAutoDeploy"),
			when: true,
		},
		{
			hasPopup: "dialog",
			icon: faHouseLaptop,
			onClick: async (): Promise<void> => {
				if (!isPremium()) {
					return;
				}
				const siteContent = signals.files.value;
				if (!configExists(siteContent)) {
					return;
				}
				const config = siteContent["settings.rth"];
				const oldName = config.home;
				let newName = await showPrompt(t("enterNewIndexPageFilename"), {
					defaultText: oldName,
				});
				newName = getValidFilename(newName);
				if (
					!newName.toLowerCase().endsWith(".php") &&
					!globals.accept.test(newName)
				) {
					void showDialog(t("thisFileTypeCannotBeHomePage"));
				} else if (
					fileExists(newName, {
						isNegative: true,
					})
				) {
					config.home = newName;
					saveHostSettings();
					void openFile(newName);
				}
			},
			requireSiteOpened: true,
			text: t("changeHomePage"),
			when: true,
		},
		{
			hasPopup: "dialog",
			icon: faShieldHalved,
			onClick: async (): Promise<void> => {
				if (!getDomain(signals.currentSite.value).includes(".")) {
					void showDialog(t("forCustomDomainsOnly"));
					return;
				} else if (!isPremium()) {
					return;
				}
				await showDialog(
					t("confirmAutomaticallyIssueCertificate"),
					{
						extraButtons: ["manually"],
						showCancel: true,
					},
					{
						manually: () => {
							openPopup("ssl");
						},
					},
				);
				void new Ssl().createCertificate();
			},
			requireSiteOpened: true,
			text: t("configureHttps"),
			when: true,
		},
		{
			hasPopup: "dialog",
			icon: faLock,
			onClick: async (): Promise<void> => {
				if (!isPremium()) {
					return;
				}
				const siteContent = signals.files.value;
				if (!configExists(siteContent)) {
					return;
				}
				const config = siteContent["settings.rth"];
				const newPassword = await showPrompt(t("enterNewPassword"));
				if (newPassword) {
					await sleep(globals.ANIMATION_WAIT_TIME);
					const newUsername = await showPrompt(
						t("enterNewUsername"),
						{
							defaultText: config.username,
						},
					);
					config.password = md5(newPassword);
					if (newUsername) {
						config.username = newUsername;
					} else {
						delete config.username;
					}
				} else {
					delete config.password;
					delete config.username;
				}
				saveHostSettings();
			},
			requireSiteOpened: true,
			text: t("setPassword"),
			when: true,
		},
		{
			hasPopup: "dialog",
			icon: faBan,
			onClick: async (): Promise<void> => {
				if (!isPremium()) {
					return;
				}
				const siteContent = signals.files.value;
				if (!configExists(siteContent)) {
					return;
				}
				const config = siteContent["settings.rth"];
				const text = await showPrompt(t("enterIpAddressesToBlock"), {
					defaultText: config.ip,
				});
				if (text) {
					config.ip = text;
				} else {
					delete config.ip;
				}
				saveHostSettings();
			},
			requireSiteOpened: true,
			text: t("limitIpAddress"),
			when: true,
		},
		{
			hasPopup: "dialog",
			icon: faBan,
			onClick: async (): Promise<void> => {
				if (!isPremium()) {
					return;
				}
				const siteContent = signals.files.value;
				if (!configExists(siteContent)) {
					return;
				}
				const config = siteContent["settings.rth"];
				const text = await showPrompt(t("enterAllowedReferrer"), {
					defaultText: config.referrer,
				});
				if (text) {
					config.referrer = text;
				} else {
					delete config.referrer;
				}
				saveHostSettings();
			},
			requireSiteOpened: true,
			text: t("limitReferrer"),
			when: true,
		},
		{
			hasPopup: "dialog",
			icon: faPowerOff,
			onClick: async (): Promise<void> => {
				const siteContent = signals.files.value;
				if (!configExists(siteContent)) {
					return;
				}
				const config = siteContent["settings.rth"];
				if (config.isDeactivated) {
					await showDialog(t("confirmActivateSite"), {
						showCancel: true,
					});
					delete config.isDeactivated;
				} else {
					await showDialog(t("confirmDeactivateSite"), {
						showCancel: true,
					});
					config.isDeactivated = true;
				}
				saveHostSettings();
			},
			requireSiteOpened: true,
			text: signals.files.value["settings.rth"]?.isDeactivated
				? t("activateSite")
				: t("deactivateSite"),
			when: true,
		},
		{
			divider: true,
			when: true,
		},
		{
			hasPopup: "dialog",
			icon: faGear,
			onClick: showSettings,
			text: t("settings"),
			when: true,
		},
		{
			hasPopup: signals.login.value.username ? "dialog" : undefined,
			icon: faKey,
			onClick: (): void => {
				openPopup("apiKeys");
			},
			text: t("apiKeys"),
			when: !!signals.login.value.username,
		},
		{
			divider: true,
			when: true,
		},
		{
			icon: faCircleQuestion,
			onClick: (): void => {
				window.open(getSupportLink());
			},
			text: t("helpAndFeedback"),
			when: !globals.isTencent,
		},
		{
			hasPopup: "dialog",
			icon: faCircleInfo,
			onClick: (): void => {
				openPopup("about");
			},
			text: t("about"),
			when: true,
		},
		{
			divider: true,
			when: true,
		},
		{
			icon: faSquareArrowUpRight,
			link: "https://www.airportal.cn/",
			text: t("airportal"),
			when: true,
		},
		{
			icon: faSquareArrowUpRight,
			link: "https://intro.limestart.cn/",
			text: t("limeStartPage"),
			when: true,
		},
	];

	const menuItemElements = menuItems.map((item, index) => {
		if (!item.when) {
			return null;
		}
		if (isDivider(item)) {
			return (
				<hr
					key={index}
					className={clsx(
						item.hideOnMobile && styles["hide-on-mobile"],
					)}
				/>
			);
		}

		const handleClick = async (
			event?: MouseEvent<HTMLDivElement>,
		): Promise<void> => {
			await closeMenu(); // await to make sure the next modal can get focus
			if (item.link) {
				window.open(item.link);
			} else if (signals.currentSite.value || !item.requireSiteOpened) {
				void item.onClick?.(event);
			} else if (item.requireSiteOpened) {
				void showDialog(t("notOpenedWebsite"));
			}
		};

		return (
			<div
				aria-haspopup={item.hasPopup}
				className={clsx(
					styles["menu-item"],
					item.hideOnMobile && styles["hide-on-mobile"],
				)}
				key={item.text || index}
				onClick={() => void handleClick()}
				onKeyDown={handleKeyboardClick(() => void handleClick())}
				role="button"
				tabIndex={0}
			>
				<FontAwesomeIcon
					icon={item.icon}
					fixedWidth
				/>
				<span>{item.text}</span>
				{!globals.isMobile && item.shortcut && (
					<span className={styles["shortcut"]}>{item.shortcut}</span>
				)}
			</div>
		);
	});

	return (
		<Dialog
			onClose={() => void closeMenu()}
			open
		>
			<div
				className={clsx(
					"overlay",
					!isClosing && "fade-in",
					isClosing && "fade-out",
				)}
				role="button"
				tabIndex={0}
				aria-label={t("closeMenu")}
				onClick={() => void closeMenu()}
				onKeyDown={handleKeyboardClick(() => void closeMenu())}
			>
				<div
					className={clsx(
						styles["menu"],
						!isClosing && "slide-in",
						isClosing && "slide-out",
					)}
				>
					{signals.login.value.username && (
						<div
							className={styles["menu-item"]}
							onClick={() => void getPremium()}
							onKeyDown={handleKeyboardClick(
								() => void getPremium(),
							)}
							role="button"
							tabIndex={0}
						>
							<FontAwesomeIcon
								icon={faCircleUser}
								fixedWidth
							/>
							<div>
								{signals.login.value.name ||
									signals.login.value.email ||
									signals.login.value.phone ||
									signals.login.value.username}
								<div className={styles["premium-info"]}>
									{signals.premiumRemaining.value > 0
										? t("premiumRemaining", {
												days: signals.premiumRemaining
													.value,
											})
										: t("noPremium")}
								</div>
							</div>
						</div>
					)}
					{menuItemElements}
				</div>
			</div>
		</Dialog>
	);
}

export default MainMenu;
